import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import {
  Trophy, Heart, MessageCircle, Share2, Eye, Hash,
  TrendingUp, ChevronUp, ExternalLink, Globe
} from "lucide-react";
import { motion } from "framer-motion";

const PLATFORM_COLORS: Record<string, string> = {
  instagram: "text-pink-400",
  twitter: "text-sky-400",
  tiktok: "text-purple-400",
  youtube: "text-red-400",
};

const PLATFORM_LABELS: Record<string, string> = {
  instagram: "Instagram",
  twitter: "Twitter / X",
  tiktok: "TikTok",
  youtube: "YouTube",
};

interface SocialProofDashboardProps {
  gameId: number;
}

export default function SocialProofDashboard({ gameId }: SocialProofDashboardProps) {
  const { data, isLoading, error } = trpc.game.socialProofDashboard.useQuery({ gameId });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-5 h-5 border-2 border-neon border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-8 text-muted-foreground text-sm">
        Unable to load social proof data.
      </div>
    );
  }

  const isHybrid = data.socialDistributionMode === "hybrid";
  const maxScore = Math.max(...data.leaderboard.map(e => isHybrid ? e.totalScore : e.directVotes), 1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Globe className="w-5 h-5 text-neon" />
            Social Proof Leaderboard
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            {isHybrid
              ? "Scores combine GamePoint votes + social media engagement"
              : "Ranked by GamePoint direct votes"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data.socialHashtag && (
            <Badge variant="outline" className="gap-1 font-mono text-xs border-neon/30 text-neon">
              <Hash className="w-3 h-3" />
              {data.socialHashtag}
            </Badge>
          )}
          <Badge
            variant={isHybrid ? "default" : "outline"}
            className={isHybrid ? "bg-gold/20 text-gold border-gold/30" : ""}
          >
            {isHybrid ? "Hybrid Scoring" : "Direct Votes"}
          </Badge>
        </div>
      </div>

      {isHybrid && (
        <Card className="glass-panel border-gold/20">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
              <span>Social Engagement Weight</span>
              <span className="font-mono font-bold text-gold">{data.socialEngagementWeight}%</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1 text-xs">
                <ChevronUp className="w-3 h-3 text-neon" />
                <span>GP Votes</span>
              </div>
              <Progress
                value={100 - (data.socialEngagementWeight ?? 50)}
                className="h-2 flex-1"
              />
              <Progress
                value={data.socialEngagementWeight ?? 50}
                className="h-2 flex-1 [&>div]:bg-gold"
              />
              <div className="flex items-center gap-1 text-xs">
                <Heart className="w-3 h-3 text-gold" />
                <span>Social</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Leaderboard */}
      <div className="space-y-3">
        {data.leaderboard.map((entry, i) => (
          <motion.div
            key={entry.submissionId}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.05 }}
          >
            <Card className={`glass-panel ${i === 0 ? "border-gold/30" : "border-border/30"}`}>
              <CardContent className="py-4">
                <div className="flex items-center gap-4">
                  {/* Rank */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 font-mono font-bold text-sm ${
                    i === 0 ? "bg-gold/20 text-gold" :
                    i === 1 ? "bg-slate-300/20 text-slate-300" :
                    i === 2 ? "bg-amber-700/20 text-amber-600" :
                    "bg-secondary text-muted-foreground"
                  }`}>
                    {i < 3 ? <Trophy className="w-4 h-4" /> : `#${entry.rank}`}
                  </div>

                  {/* Content preview */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {entry.submissionType === "image" ? (
                        <span className="text-muted-foreground italic">Image submission</span>
                      ) : (
                        entry.content.slice(0, 80)
                      )}
                    </p>
                    {entry.caption && (
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{entry.caption}</p>
                    )}

                    {/* Social post link */}
                    {entry.socialPostUrl && (
                      <a
                        href={entry.socialPostUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center gap-1 text-xs mt-1 hover:underline ${
                          PLATFORM_COLORS[entry.socialPlatform || ""] || "text-neon"
                        }`}
                      >
                        <ExternalLink className="w-3 h-3" />
                        {PLATFORM_LABELS[entry.socialPlatform || ""] || "Social Post"}
                      </a>
                    )}
                  </div>

                  {/* Score breakdown */}
                  <div className="flex items-center gap-4 shrink-0">
                    {/* Direct votes */}
                    <div className="text-center">
                      <div className="flex items-center gap-1 text-neon">
                        <ChevronUp className="w-3.5 h-3.5" />
                        <span className="font-mono font-bold text-sm">{entry.directVotes}</span>
                      </div>
                      <span className="text-[10px] text-muted-foreground">GP Votes</span>
                    </div>

                    {isHybrid && (
                      <>
                        <Separator orientation="vertical" className="h-8 bg-border/30" />

                        {/* Social score */}
                        <div className="text-center">
                          <div className="flex items-center gap-1 text-gold">
                            <Heart className="w-3.5 h-3.5" />
                            <span className="font-mono font-bold text-sm">{entry.socialScore}</span>
                          </div>
                          <span className="text-[10px] text-muted-foreground">Social</span>
                        </div>

                        {/* Social engagement breakdown */}
                        {entry.latestEngagement && (
                          <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                            <span className="flex items-center gap-0.5">
                              <Heart className="w-2.5 h-2.5" />{entry.latestEngagement.likes}
                            </span>
                            <span className="flex items-center gap-0.5">
                              <MessageCircle className="w-2.5 h-2.5" />{entry.latestEngagement.comments}
                            </span>
                            <span className="flex items-center gap-0.5">
                              <Share2 className="w-2.5 h-2.5" />{entry.latestEngagement.shares}
                            </span>
                            <span className="flex items-center gap-0.5">
                              <Eye className="w-2.5 h-2.5" />{entry.latestEngagement.views}
                            </span>
                          </div>
                        )}

                        <Separator orientation="vertical" className="h-8 bg-border/30" />

                        {/* Total score */}
                        <div className="text-center">
                          <div className="flex items-center gap-1">
                            <TrendingUp className="w-3.5 h-3.5 text-neon" />
                            <span className="font-mono font-bold text-sm text-foreground">{entry.totalScore}</span>
                          </div>
                          <span className="text-[10px] text-muted-foreground">Total</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Score bar */}
                <div className="mt-3">
                  <div className="w-full h-1.5 bg-secondary rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        i === 0 ? "bg-gradient-to-r from-teal to-gold" : "bg-neon/60"
                      }`}
                      style={{
                        width: `${((isHybrid ? entry.totalScore : entry.directVotes) / maxScore) * 100}%`,
                      }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}

        {data.leaderboard.length === 0 && (
          <div className="text-center py-12 text-muted-foreground text-sm">
            No submissions yet. Be the first to enter!
          </div>
        )}
      </div>

      {/* How Hybrid Scoring Works */}
      {isHybrid && (
        <Card className="glass-panel border-border/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">How Hybrid Scoring Works</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-xs text-muted-foreground">
            <p>
              <strong className="text-foreground">Direct Votes</strong> are cast on GamePoint by clicking the vote button on any submission.
            </p>
            <p>
              <strong className="text-foreground">Social Score</strong> is calculated from engagement on your linked social media post:
              Likes (×1) + Comments (×2) + Shares (×3).
            </p>
            <p>
              <strong className="text-foreground">Total Score</strong> = Direct Votes + (Social Score × {(data.socialEngagementWeight ?? 50)}%).
              The game creator sets the social engagement weight.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
